import { useTranslate } from '@tolgee/react';

/**
 * Centralizes all copy for the new login/signup experience.
 *
 * Strings go through Tolgee (`t(key, defaultValue)`) so they integrate with the
 * app's i18n + SSR `detectLanguage` flow and translate when the keys are present
 * on the Tolgee platform. English defaults are inlined here so the screen always
 * renders; ES/NO/FR/DE values come from the design handoff and are uploaded to
 * Tolgee under these keys.
 */
export function useAuthStrings() {
  const { t } = useTranslate();

  return {
    signupTitle: t('auth.signupTitle', 'Signup'),
    loginTitle: t('auth.loginTitle', 'Login'),

    email: t('auth.email', 'Email'),
    emailPh: t('auth.emailPlaceholder', 'you@school.edu'),
    continue: t('auth.continue', 'Continue'),
    secureHint: t(
      'auth.secureHint',
      "We'll email you a secure sign-in link. No password to remember.",
    ),
    invalidEmail: t('auth.invalidEmail', 'Please enter a valid email address.'),
    orContinue: t('auth.orContinueWith', 'or continue with'),
    otherProviders: t('auth.otherProviders', 'Other providers'),
    hideOther: t('auth.fewerOptions', 'Fewer options'),

    haveAccount: t('auth.haveAccount', 'Already teaching with Curipod?'),
    login: t('auth.logIn', 'Log in'),
    newHere: t('auth.newHere', 'New to Curipod?'),
    signup: t('auth.signUpFree', "Sign up — it's free"),

    legalPre: t('auth.legalPre', "By continuing, you agree to Curipod's"),
    terms: t('auth.terms', 'Terms of Service'),
    and: t('auth.and', 'and'),
    privacy: t('auth.privacy', 'Privacy Policy'),
    legalPost: t('auth.legalPost', ', and to receive periodic emails with updates.'),

    // Accessibility labels for the per-box code inputs (suffixed with the position
    // number in the component, e.g. "Character 1").
    codeCharLabel: t('auth.codeCharLabel', 'Character'),
    joinDigitLabel: t('auth.joinDigitLabel', 'Digit'),

    // Magic-link code-entry screen
    magicTitle: t('auth.magicTitle', 'Check your email'),
    codeSub: t('auth.codeSub', 'We sent a 6-character code to'),
    didntGet: t('auth.didntGet', "Didn't get it?"),
    sendNew: t('auth.sendNewCode', 'Send a new code'),
    codeResent: t('auth.codeResent', 'Code sent ✓'),
    tryAnother: t('auth.tryAnother', 'Try another way'),
    useDiff: t('auth.useDifferentEmail', 'Use a different email'),
    signingIn: t('auth.signingIn', 'Signing you in…'),
    signingInSub: t('auth.signingInSub', 'One moment — setting up your dashboard.'),

    // Inline magic-link errors (the backend collapses verify failures to one
    // reason, so a single generic message + the resend affordance is the right UX).
    errTooManyRequests: t(
      'auth.err.tooManyRequests',
      'Too many requests — please wait a minute and try again.',
    ),
    errSendCodeFailed: t(
      'auth.err.sendCodeFailed',
      'We couldn’t send a code right now. Please try again.',
    ),
    errCodeInvalid: t(
      'auth.err.codeInvalidInline',
      'That code didn’t work or has expired — request a new code below.',
    ),
    // Shown with the backend reference id when a code can't be sent, so the user
    // can quote it to support.
    errSupportRef: t(
      'auth.err.supportRef',
      'If this keeps happening, contact support with ref',
    ),

    // Student join code
    joinCode: t('auth.joinCode', 'Enter join code'),
    joinTitle: t('auth.joinTitle', 'Join your class'),
    joinSub: t('auth.joinSub', "Enter the 6-digit code from your teacher's screen."),
    joinBtn: t('auth.joinBtn', 'Join now'),
    joinHint: t('auth.joinHint', "Students don't need an account — just the code."),
    back: t('auth.back', 'Back'),
  };
}

export type AuthStrings = ReturnType<typeof useAuthStrings>;
